/**
 * Guías de uso de Nukli — paso a paso, con un plano de la ventana en cada paso.
 *
 * Son el segundo apartado del blog (pestañas Blog / Guías), pero contenido de
 * otra naturaleza: los artículos persiguen búsquedas de Google; estas guías
 * persiguen a la persona que YA tiene la app abierta y no sabe dónde pulsar.
 * Están escritas para leerse en voz alta a alguien de 70 años: frases cortas,
 * un paso por idea y "dónde está" antes que "por qué".
 *
 * Los datos son estructurados (no HTML) a propósito: el mismo objeto pinta la
 * página web y alimenta el catálogo que la app consulta cuando el asistente
 * tiene que recomendar una guía (`scripts/generate-help-guides.js`).
 *
 * Idiomas: ca / es / en. El resto de locales del sitio caen a `en`.
 *
 * Campos de cada guía:
 *   slug      URL: /guides/<slug>
 *   id        identificador estable para el catálogo de la app (NO cambiarlo)
 *   category  clave de CATEGORIES
 *   level     basic | medium | advanced
 *   plan      free | pro | proplus  (qué plan hace falta)
 *   topics    palabras que disparan la recomendación, en los tres idiomas
 *   <lang>.answer  respuesta corta que el asistente puede leer en el chat
 *   <lang>.steps   [{ t: título, d: detalle, zone?: clave de ZONES, tip? }]
 */

import { GUIDES_START } from './data/start';
import { GUIDES_AI } from './data/ai';
import { GUIDES_WRITING } from './data/writing';
import { GUIDES_INBOX } from './data/inbox';
import { GUIDES_MORE } from './data/more';

export const CATEGORIES = [
  {
    key: 'start',
    icon: 'rocket',
    ca: { name: 'Per començar', desc: 'Instal·lar-la, connectar el correu i entendre la pantalla.' },
    es: { name: 'Para empezar', desc: 'Instalarla, conectar el correo y entender la pantalla.' },
    en: { name: 'Getting started', desc: 'Install it, connect your email and learn the screen.' },
  },
  {
    key: 'ai',
    icon: 'sparkles',
    ca: { name: "L'assistent", desc: 'Què li pots demanar i com respondre molt més ràpid.' },
    es: { name: 'El asistente', desc: 'Qué le puedes pedir y cómo responder mucho más rápido.' },
    en: { name: 'The assistant', desc: 'What you can ask it and how to reply much faster.' },
  },
  {
    key: 'writing',
    icon: 'feather',
    ca: { name: 'Escriure i enviar', desc: 'Traduir, corregir, dictar, programar i adjuntar.' },
    es: { name: 'Escribir y enviar', desc: 'Traducir, corregir, dictar, programar y adjuntar.' },
    en: { name: 'Writing & sending', desc: 'Translate, proofread, dictate, schedule and attach.' },
  },
  {
    key: 'inbox',
    icon: 'inbox',
    ca: { name: 'Ordre a la safata', desc: 'Trobar-ho tot, no oblidar res i treure’t el soroll de sobre.' },
    es: { name: 'Orden en la bandeja', desc: 'Encontrarlo todo, no olvidar nada y quitarte el ruido.' },
    en: { name: 'A tidy inbox', desc: 'Find everything, forget nothing, cut the noise.' },
  },
  {
    key: 'more',
    icon: 'settings',
    ca: { name: 'Més coses', desc: 'Compte, privacitat, mòbil, dreceres i companys.' },
    es: { name: 'Más cosas', desc: 'Cuenta, privacidad, móvil, atajos y compañeros.' },
    en: { name: 'More', desc: 'Account, privacy, mobile, shortcuts and teammates.' },
  },
];

export const GUIDES = [
  ...GUIDES_START,
  ...GUIDES_AI,
  ...GUIDES_WRITING,
  ...GUIDES_INBOX,
  ...GUIDES_MORE,
];

/**
 * Textos de la propia página de guías.
 *
 * Van aquí y no en el i18n del sitio (19 idiomas) a propósito: las guías solo
 * existen en ca/es/en, así que un visitante polaco las lee en inglés — y los
 * rótulos alrededor tienen que ir en ese mismo inglés, no en polaco. Mantener
 * las dos cosas en la misma tabla evita páginas mitad y mitad.
 */
export const GUIDE_UI = {
  ca: {
    badge: 'Guies pas a pas',
    title: 'Aprèn a fer servir Nukli',
    lead: 'Guies curtes, amb dibuixos i sense paraules rares. Pensades perquè qualsevol pugui seguir-les, tingui l’edat que tingui.',
    allGuides: 'Totes les guies',
    guides: 'guies',
    minutes: 'min',
    read: 'Obre la guia',
    steps: 'Pas a pas',
    examples: 'Frases que li pots dir',
    tips: 'Consells',
    faq: 'Dubtes freqüents',
    summary: 'En resum',
    related: 'Guies relacionades',
    back: 'Totes les guies',
    mapCaption: 'Les cinc zones de la finestra de Nukli.',
    stepCaption: 'On és, dins de la finestra.',
    ctaTitle: 'Encara no tens Nukli?',
    ctaLead: 'Prova-la gratis: el correu de sempre, amb un assistent al costat.',
    ctaButton: 'Descarrega Nukli',
    level: { basic: 'Bàsica', medium: 'Intermèdia', advanced: 'Avançada' },
    plan: { free: 'Pla gratuït', pro: 'Pla Pro', proplus: 'Pla Pro+' },
    notFound: 'Aquesta guia no existeix.',
    blogTab: 'Blog',
    guidesTab: 'Guies',
    fromBlogLead: 'Tenim guies amb dibuixos per a cada cosa que es pot fer amb Nukli.',
  },
  es: {
    badge: 'Guías paso a paso',
    title: 'Aprende a usar Nukli',
    lead: 'Guías cortas, con dibujos y sin palabras raras. Pensadas para que cualquiera pueda seguirlas, tenga la edad que tenga.',
    allGuides: 'Todas las guías',
    guides: 'guías',
    minutes: 'min',
    read: 'Abrir la guía',
    steps: 'Paso a paso',
    examples: 'Frases que le puedes decir',
    tips: 'Consejos',
    faq: 'Dudas frecuentes',
    summary: 'En resumen',
    related: 'Guías relacionadas',
    back: 'Todas las guías',
    mapCaption: 'Las cinco zonas de la ventana de Nukli.',
    stepCaption: 'Dónde está, dentro de la ventana.',
    ctaTitle: '¿Todavía no tienes Nukli?',
    ctaLead: 'Pruébala gratis: el correo de siempre, con un asistente al lado.',
    ctaButton: 'Descargar Nukli',
    level: { basic: 'Básica', medium: 'Intermedia', advanced: 'Avanzada' },
    plan: { free: 'Plan gratuito', pro: 'Plan Pro', proplus: 'Plan Pro+' },
    notFound: 'Esta guía no existe.',
    blogTab: 'Blog',
    guidesTab: 'Guías',
    fromBlogLead: 'Tenemos guías con dibujos para cada cosa que se puede hacer con Nukli.',
  },
  en: {
    badge: 'Step-by-step guides',
    title: 'Learn how to use Nukli',
    lead: 'Short guides, with drawings and no jargon. Written so anyone can follow them, at any age.',
    allGuides: 'All guides',
    guides: 'guides',
    minutes: 'min',
    read: 'Open the guide',
    steps: 'Step by step',
    examples: 'Things you can say to it',
    tips: 'Tips',
    faq: 'Common questions',
    summary: 'In short',
    related: 'Related guides',
    back: 'All guides',
    mapCaption: 'The five areas of the Nukli window.',
    stepCaption: 'Where it is, inside the window.',
    ctaTitle: 'Not using Nukli yet?',
    ctaLead: 'Try it free: the same email you already have, with an assistant beside it.',
    ctaButton: 'Download Nukli',
    level: { basic: 'Basic', medium: 'Intermediate', advanced: 'Advanced' },
    plan: { free: 'Free plan', pro: 'Pro plan', proplus: 'Pro+ plan' },
    notFound: 'That guide does not exist.',
    blogTab: 'Blog',
    guidesTab: 'Guides',
    fromBlogLead: 'We have illustrated guides for everything you can do with Nukli.',
  },
};

/** Etiquetas de las cinco zonas del plano de la ventana (GuideFigure). */
export const ZONE_LABELS = {
  ca: ['Barra d’icones', 'Bústies', 'Llista de correus', 'Correu obert', 'Assistent'],
  es: ['Barra de iconos', 'Buzones', 'Lista de correos', 'Correo abierto', 'Asistente'],
  en: ['Icon bar', 'Mailboxes', 'Email list', 'Open email', 'Assistant'],
};

const LANGS = ['ca', 'es', 'en'];

/** El idioma del sitio puede ser cualquiera de los 19; las guías solo tienen 3. */
export function guideLang(locale) {
  return LANGS.includes(locale) ? locale : 'en';
}

export function getGuide(slug) {
  return GUIDES.find((g) => g.slug === slug) || null;
}

export function localizedGuide(guide, locale) {
  if (!guide) return null;
  const l = guide[guideLang(locale)] || guide.en;
  return {
    slug: guide.slug,
    id: guide.id,
    category: guide.category,
    icon: guide.icon,
    level: guide.level || 'basic',
    plan: guide.plan || 'free',
    minutes: guide.minutes || Math.max(2, Math.ceil((l.steps || []).length * 0.8)),
    // Solo la guía que explica la pantalla abre con el plano entero rotulado.
    overview: !!guide.overview,
    ...l,
  };
}

export function localizedCategory(cat, locale) {
  const l = cat[guideLang(locale)] || cat.en;
  return { key: cat.key, icon: cat.icon, ...l };
}

/** Guías de la misma categoría, para el pie de cada página. */
export function siblingGuides(slug, locale, max = 3) {
  const guide = getGuide(slug);
  if (!guide) return [];
  return GUIDES.filter((g) => g.category === guide.category && g.slug !== slug)
    .slice(0, max)
    .map((g) => localizedGuide(g, locale));
}
